// FILE: TagsClient.ts
// Purpose: HTTP wrapper around `/api/admin/tags*`, mirroring MemoriesClient's
//          shape (plain object, static methods) over the shared `adminFetch`.
// Layer: Web client
// Exports: `TagsClient`

import type {
  AdminTagCreateRequest,
  AdminTagListEntry,
  AdminTagResponse,
  AdminTagUpdateRequest,
  AdminTagsResponse,
  ProjectId,
  Tag,
  TagId,
} from "@taskin-code/contracts";

import { adminFetch } from "../lib/adminFetch";

/**
 * Only the vocabulary. Attaching a tag to something travels on that thing's own
 * route — `dominio:android` on a memory is an edit to the memory — so there is
 * deliberately no `assign` here to reach for by mistake.
 */
export const TagsClient = {
  /**
   * The vocabulary, already ordered `namespace, slug` by the server. Retired
   * tags only when asked for — they exist in the listing so that `restore` is
   * reachable, and nowhere else.
   */
  async list(options?: {
    readonly includeArchived?: boolean;
  }): Promise<ReadonlyArray<AdminTagListEntry>> {
    const query = options?.includeArchived === true ? "?includeArchived=1" : "";
    const result = await adminFetch<AdminTagsResponse>(`/api/admin/tags${query}`);
    return result.tags;
  },

  async create(input: AdminTagCreateRequest): Promise<Tag> {
    const result = await adminFetch<AdminTagResponse>("/api/admin/tags", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.tag;
  },

  async update(tagId: TagId, patch: AdminTagUpdateRequest): Promise<Tag> {
    const result = await adminFetch<AdminTagResponse>(`/api/admin/tags/${tagId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return result.tag;
  },

  /** Retire a tag. Its assignments survive, so `restore` brings the chips back. */
  async archive(tagId: TagId): Promise<void> {
    await adminFetch<void>(`/api/admin/tags/${tagId}`, { method: "DELETE" });
  },

  /**
   * A project's tags.
   *
   * The one assignment this client owns, and the exception the routes explain:
   * a project has no HTTP admin surface of its own, and its tags could not ride
   * an orchestration command because `tag_assignments` is not event-sourced.
   * Gated by `p.edit` on that project, never by `t.edit`.
   */
  async projectTags(projectId: ProjectId): Promise<ReadonlyArray<TagId>> {
    const result = await adminFetch<{ tagIds: ReadonlyArray<TagId> }>(
      `/api/admin/tags/projects/${projectId}`,
    );
    return result.tagIds;
  },

  async setProjectTags(
    projectId: ProjectId,
    tagIds: ReadonlyArray<TagId>,
  ): Promise<ReadonlyArray<TagId>> {
    const result = await adminFetch<{ tagIds: ReadonlyArray<TagId> }>(
      `/api/admin/tags/projects/${projectId}`,
      {
        method: "PUT",
        body: JSON.stringify({
          target: { type: "project", scopeId: "", id: projectId },
          tagIds,
        }),
      },
    );
    return result.tagIds;
  },

  /**
   * A local command's tags — the second assignment this client owns, for the
   * same reason as a project's: the catalog travels as an orchestration
   * command, and `tag_assignments` is not event-sourced.
   *
   * These are what decide where a published command ranks as a template.
   * Gated by `p.local.manage` on the project, never by `t.edit`.
   */
  async localCommandTags(projectId: ProjectId, actionId: string): Promise<ReadonlyArray<TagId>> {
    const result = await adminFetch<{ tagIds: ReadonlyArray<TagId> }>(
      `/api/admin/tags/local-commands/${encodeURIComponent(projectId)}/${encodeURIComponent(actionId)}`,
    );
    return result.tagIds;
  },

  async setLocalCommandTags(
    projectId: ProjectId,
    actionId: string,
    tagIds: ReadonlyArray<TagId>,
  ): Promise<ReadonlyArray<TagId>> {
    const result = await adminFetch<{ tagIds: ReadonlyArray<TagId> }>(
      `/api/admin/tags/local-commands/${encodeURIComponent(projectId)}/${encodeURIComponent(actionId)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          target: { type: "local_command", scopeId: projectId, id: actionId },
          tagIds,
        }),
      },
    );
    return result.tagIds;
  },

  async restore(tagId: TagId): Promise<Tag> {
    const result = await adminFetch<AdminTagResponse>(`/api/admin/tags/${tagId}/restore`, {
      method: "POST",
    });
    return result.tag;
  },
};
